import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import {
  AccountCircle,
  Email,
  Phone,
  Business,
  Edit,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Button from "../components/ui/Button";
import Avatar from "../components/ui/Avatar";

export default function ViewProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "bg-purple-100 text-purple-700 border-purple-200";
      case "hr":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "employee":
        return "bg-green-100 text-green-700 border-green-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-primary mb-2">My Profile</h1>
            <p className="text-gray-600">View your profile information</p>
          </div>
          <Button
            variant="primary"
            onClick={() => navigate("/profile")}
            className="flex items-center gap-2"
          >
            <Edit className="w-5 h-5" />
            Edit Profile
          </Button>
        </div>

        {/* Profile Header Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-6"
        >
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6 p-6">
            <div className="relative">
              <Avatar src={user?.profile_image} name={user?.name} size="xl" />
              <div className="absolute bottom-0 right-0 w-6 h-6 bg-green-500 rounded-full border-4 border-white"></div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-3xl font-bold text-primary mb-2">
                {user?.name || "User"}
              </h2>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                <span
                  className={`px-4 py-1 rounded-full text-sm font-semibold border ${getRoleColor(
                    user?.role
                  )}`}
                >
                  {user?.role?.toUpperCase() || "EMPLOYEE"}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Personal Information Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card mb-6"
        >
          <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
            Personal Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Email className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-1">Email Address</p>
                <p className="text-base font-semibold text-gray-700">
                  {user?.email || "N/A"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                <Phone className="w-6 h-6 text-secondary" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-1">Phone Number</p>
                <p className="text-base font-semibold text-gray-700">
                  {user?.phone || "Not provided"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                <AccountCircle className="w-6 h-6 text-accent" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-1">User ID</p>
                <p className="text-base font-semibold text-gray-700">
                  {user?._id || user?.id || "N/A"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Business className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-1">Account Status</p>
                <p className="text-base font-semibold text-green-600">Active</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Account Details Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <h3 className="text-xl font-bold text-primary mb-6 pb-4 border-b border-gray-200">
            Account Details
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="text-sm text-gray-500 mb-1">Member Since</p>
                <p className="text-base font-semibold text-gray-700">
                  {user?.outlookAccountCreatedAt
                    ? new Date(user.outlookAccountCreatedAt).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )
                    : user?.createdAt
                    ? new Date(user.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : "N/A"}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="text-sm text-gray-500 mb-1">Last Updated</p>
                <p className="text-base font-semibold text-gray-700">
                  {user?.updatedAt
                    ? new Date(user.updatedAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
